/**
 * `/login` — email + password against `POST /api/auth/sign-in/email`.
 *
 * Design §2.2 also lists magic link and "Sign in with Google/Microsoft". Neither
 * is configured on the server's better-auth instance (`packages/auth` wires
 * `organization`, `jwt`, `apiKey` and `bearer` — no social providers, no
 * `magicLink`), so neither is offered.
 *
 * They used to be offered as three DISABLED chips tooltipped "better-auth
 * socialProviders". Sweep, 2026-08-29: a dead button on the first screen of the
 * product, labelled with the name of a library plugin, is the worst possible
 * first impression — it reads as broken rather than as unbuilt. One plain
 * sentence replaces all three.
 *
 * The server's answer is surfaced verbatim rather than rewritten into something
 * friendlier — "Sign-in failed" over a 501 would send someone hunting for a typo
 * in their password.
 *
 * ## The cached-null-session trap (found in production, 2026-08-29)
 *
 * `_authed.beforeLoad` resolves the session with `ensureQueryData`, which serves
 * a CACHED value while it is fresh. Landing on a protected route while signed
 * out therefore caches `session: null` for the query's `staleTime` — and a
 * sign-in that succeeded a second later navigated straight back into that stale
 * `null`, bounced to `/login`, and looked to the user like the button did
 * nothing. (Then they clicked it again, and again, and hit the auth rate limit,
 * which is how this was reported.)
 *
 * So the session cache is REMOVED — not merely invalidated — before navigating.
 * Removal forces `ensureQueryData` to fetch; invalidation alone leaves a
 * resolved-but-stale entry that `ensureQueryData` will still hand back.
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Field, fieldAria, Input } from '@crm/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { signInWithPassword } from '../api/auth.ts';
import { queryKeys } from '../api/keys.ts';
import { parseLoginSearch } from '../lib/search.ts';
import { zodFormResolver } from '../lib/zod-form.ts';

const loginSchema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});

type LoginValues = z.output<typeof loginSchema>;

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: parseLoginSearch,
});

function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues, unknown, LoginValues>({
    resolver: zodFormResolver<LoginValues, LoginValues>(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const submit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await signInWithPassword(values);
      // See the header note: a stale `null` here is what makes a SUCCESSFUL
      // sign-in look like a dead button.
      queryClient.removeQueries({ queryKey: queryKeys.session() });
      await navigate({ to: redirect ?? '/today' });
    } catch (error) {
      setServerError(error instanceof Error ? error.message : 'Sign-in failed');
    }
  });

  return (
    <div className="flex h-full items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Field id="email" label="Email" error={errors.email?.message}>
              <Input
                {...register('email')}
                {...fieldAria('email', { error: errors.email?.message })}
                type="email"
                autoComplete="email"
                autoFocus
              />
            </Field>

            <Field id="password" label="Password" error={errors.password?.message}>
              <Input
                {...register('password')}
                {...fieldAria('password', { error: errors.password?.message })}
                type="password"
                autoComplete="current-password"
              />
            </Field>

            {serverError === null ? null : (
              <p role="alert" className="text-sm font-medium text-destructive">
                {serverError}
              </p>
            )}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <p className="pt-1 text-xs text-muted-foreground">
              Signing in with Google, Microsoft or a one-time email link is coming.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
